/**
 * cart.js — Cart Optional Feature (Fogarty)
 *
 * PURPOSE:
 *   Orchestrates the in-widget shopping cart. Owns the cartId, fulfils the
 *   header cart icon (ws-cart-open), and turns WineCard add/update/remove
 *   events into BE calls, then broadcasts the line count so the header badge
 *   and the wine cards reflect the latest cart.
 *
 * ARCHITECTURE:
 *   The widget never calls Shopify directly — every call goes FE → BE → Shopify
 *   via core/api.js (public /api/v1/integrations/shopify/* endpoints; tenant
 *   resolved from the X-Tenant-ID header). See PENDING_FOGARTY_PARITY.md.
 *
 * ENABLED BY:
 *   tenant.features.shopify = true AND tenant.ui_features.show_cart = true.
 *   Wired from variants/fogartywinery/index.js register().
 *
 * EVENTS:
 *   in:  ws-cart-open, ws-cart-add {variantId, quantity, fromDrawer?},
 *        ws-cart-update {variantId, quantity, fromCart?, fromDrawer?},
 *        ws-cart-remove {variantId, fromCart?, fromDrawer?}
 *   out: ws-cart-count {count}   (Header badge + WineCard controls listen)
 *
 * fromDrawer:
 *   Tasting-notes drawer add/update/remove must NOT post "Here is your order
 *   summary" (old Fogarty tasting footer never called showCartsProducts).
 *
 * cartId PERSISTENCE:
 *   Stored per tenant in localStorage (key ws_cart_<tenantId>), mirroring
 *   core/session.js, so the cart survives reload/minimize/revisit.
 *
 * REFERENCE (old code):
 *   thomasfogarty/script.js createCart/addToCart/removeFromCart (3016-3300)
 *
 * EXPORTS:
 *   init() — subscribes to cart events; idempotent.
 */

import * as api from "../../core/api.js";
import { on, emit, getState, setState, addMessage, getMessages, removeMessage } from "../../core/state.js";
import { getTenantId } from "../../core/configAccessors.js";
import { error } from "../../core/env.js";

const keyFor = (tenantId) => `ws_cart_${tenantId || "default"}`;

function loadStoredCartId() {
  try {
    return localStorage.getItem(keyFor(getTenantId())) || null;
  } catch {
    return null;
  }
}

// Persist cartId in state + localStorage (the cart's conversation handle, not
// an auth credential — localStorage is the right store, same as session.js).
function persistCartId(id) {
  setState({ cart: { ...getState().cart, id } });
  try {
    if (id) localStorage.setItem(keyFor(getTenantId()), id);
  } catch {
    /* storage blocked — cartId stays in memory for this page only */
  }
}

function getCartId() {
  return getState().cart.id || loadStoredCartId();
}

// Mirror a BE cart ({ cartId, items[], cart{} }) into state and tell listeners
// the new line count. Returns the normalized { id, items }.
function applyCart(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const id = cart?.cartId ?? getState().cart.id ?? null;
  // Keep the summary (subTotal/total/checkoutUrl) so CartPanel can re-render
  // itself live after a line edit without re-fetching.
  const summary = cart?.cart || null;
  setState({ cart: { ...getState().cart, id, items, summary } });
  const count = items.reduce((n, it) => n + (Number(it.quantity) || 0), 0);
  emit("ws-cart-count", { count });
  return { id, items };
}

export function syncCartWithWebsite(){
  const cart = getCartInfo();
  const cartCount = cart?.items?.length ?? 0;
  if(cartCount){
    setTimeout(()=>window.location.reload(),3000);
  }
}

export function getCartInfo(){
  return getState().cart;
}

function showText(text) {
  // A chat turn's typing indicator (e.g. still fetching the follow-up
  // recommendations for the previous message) can still be showing when the
  // user clicks Add to cart / Proceed to Checkout — those are independent of
  // the chat's streaming state. Clear it so a cart message never gets
  // appended right after a stale "assistant is typing" bubble that looks
  // like it was caused by the cart action.
  emit("ws-chat-typing", { active: false });
  addMessage({ role: "bot", kind: "text", data: { text } });
}

export function showEmpty() {
  emit("ws-cart-count", { count: 0 });
  showText(getState().cart.emptyMessage);
}

export function showBasicRecommendation(){
  addMessage({ role: 'bot', kind: 'text', data: { text: 'Would you like to explore some wines?'}})
  addMessage({ 
  role: 'bot', 
  kind:'prompts', 
  data: { 
    items: [' Show me all red wines.', ' Show me all white wines.', ' Show me all rose wines'] 
  }});
}

function showError() {
  showText("Sorry, we couldn't update your cart. Please try again.");
}

const ORDER_SUMMARY_TEXT = "Here is your order summary";

// Keep ONE order summary in the chat. Every cart panel re-renders live from the
// same state.cart, so an earlier "Here is your order summary" + panel would show
// the same cart twice — or its in-panel empty state right above a second "cart
// is empty" message. removeMessage drops the DOM node AND the persisted
// transcript, so the stale copy doesn't come back after Fogarty's reload.
function clearOrderSummaries() {
  const messages = getMessages();
  const stale = [];
  messages.forEach((m, i) => {
    if (m.kind !== "cart") return;
    stale.push(m.id);
    const prev = messages[i - 1];
    if (prev?.kind === "text" && prev.data?.text === ORDER_SUMMARY_TEXT) stale.push(prev.id);
  });
  stale.forEach((id) => removeMessage(id));
}

// Parity with old showCartsProducts: after a cart change driven from a wine card
// (or View cart), post "Here is your order summary" + the cart panel — or the
// empty-cart message — replacing any earlier summary. Edits made inside a cart
// panel pass fromCart and re-render that panel in place instead.
function showOrderSummary(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  clearOrderSummaries();
  if (!items.length) {
    showText(getState().cart.emptyMessage);
    return;
  }
  showText(ORDER_SUMMARY_TEXT);
  addMessage({ role: "bot", kind: "cart", data: { items, cart } });
}

// Wineengine Build Wine Box: when active, cart mutations refresh the drawer
// instead of posting an order-summary panel. When interceptCart is set, View
// Basket / header cart / Add to Basket open the build-box cart UI (Your Cart
// with Monthly|One-off) instead of the Fogarty CartPanel.
function isBuildBoxActive() {
  return !!getState().buildBox?.active;
}

function useBuildBoxCartUi() {
  return !!getState().buildBox?.interceptCart;
}

function showCartAfterMutation(cart, oldCount, { fromCart = false, fromDrawer = false } = {}) {
  // Success-only hook, after applyCart has persisted the updated state.
  // Fogarty uses it for its production cart-update notice and reopen flow.
  emit("ws-cart-mutated", { cart, fromCart, fromDrawer });
  if (isBuildBoxActive()) {
    afterBuildBoxMutation(cart, oldCount);
    return;
  }
  // Wineengine: same UI as cart icon (old showCartsProducts) — not Fogarty panel.
  if (useBuildBoxCartUi()) {
    if (!fromCart) emit("ws-build-box-open-cart");
    return;
  }
  // Old tasting-notes drawer: badge + drawer footer only — no order summary.
  if (fromDrawer) return;
  if (!fromCart) showOrderSummary(cart);
}

function bottleCountOf(items) {
  return (items || []).reduce((n, it) => n + (Number(it.quantity) || 0), 0);
}

function afterBuildBoxMutation(cart, oldCount) {
  emit("ws-build-box-cart-mutated", { cart, oldCount });
}

function sellingPlanIdForAdd() {
  const bb = getState().buildBox || {};
  if (bb.active && bb.frequency === "monthly" && bb.sellingPlanId) {
    return bb.sellingPlanId;
  }
  return null;
}

let openingCart = false;
let cartRenderDelayMs = 0;

async function openCart() {
  // Wineengine: always route header / View Basket through build-box cart UI.
  if (getState().buildBox?.interceptCart) {
    emit("ws-build-box-open-cart");
    return;
  }
  if (openingCart) return;
  const cartId = getCartId();
  if (!cartId) {
    showEmpty();
    return;
  }
  openingCart = true;
  emit("ws-chat-typing", { active: true, scroll: true });
  try {
    const cart = await api.getCart(cartId);
    applyCart(cart);
    // Fogarty's old showCartsProducts waits 500ms after fetching a non-empty
    // cart. Other Shopify variants retain their existing render timing.
    if (cartRenderDelayMs && cart?.items?.length) {
      await new Promise(resolve => setTimeout(resolve, cartRenderDelayMs));
    }
    // CartPanel reads data.items[] and data.cart.cart{subTotal,total,checkoutUrl}.
    showOrderSummary(cart);
  } catch (e) {
    error("[cart] open failed", e);
    showError();
  } finally {
    emit("ws-chat-typing", { active: false });
    openingCart = false;
  }
}

async function addItem({ variantId, quantity = 1, fromDrawer = false } = {}) {
  if (!variantId) return;
  emit("ws-cart-busy", { active: true });
  const oldCount = bottleCountOf(getState().cart?.items);
  try {
    const cartId = getCartId();
    const sellingPlanId = sellingPlanIdForAdd();
    // createCart with a variant both creates the cart and adds the first line;
    // an existing cart just gets the line added.
    const cart = cartId
      ? await api.addCartItem({ cartId, variantId, quantity, sellingPlanId })
      : await api.createCart({ variantId, quantity, sellingPlanId });
    if (cart?.cartId) persistCartId(cart.cartId);
    applyCart(cart);
    showCartAfterMutation(cart, oldCount, { fromDrawer });
  } catch (e) {
    error("[cart] add failed", e);
    showError();
  } finally {
    emit("ws-cart-busy", { active: false });
  }
}

async function updateItem({ variantId, quantity, fromCart = false, fromDrawer = false } = {}) {
  const cartId = getCartId();
  const entireState = getState();
  if (!cartId || !variantId) return;
  if (Number(quantity) <= 0) {
    removeItem({ variantId, fromCart, fromDrawer });
    return;
  }
  emit("ws-cart-busy", { active: true });
  const oldCount = bottleCountOf(entireState.cart?.items);
  try {
    // Update by line id when we have it (from the current cart), else by variant.
    const line = (getState().cart.items || []).find(
      (it) => String(it.productVariantId) === String(variantId)
    );
    const lines = [{ ...(line?.id ? { id: line.id } : {}), variantId, quantity }];
    const cart = await api.updateCartItem({ cartId, lines });
    applyCart(cart);
    showCartAfterMutation(cart, oldCount, { fromCart, fromDrawer });
  } catch (e) {
    error("[cart] update failed", e);
    showError();
  } finally {
    emit("ws-cart-busy", { active: false });
  }
}

async function removeItem({ variantId, fromCart = false, fromDrawer = false } = {}) {
  const cartId = getCartId();
  if (!cartId || !variantId) return;
  emit("ws-cart-busy", { active: true });
  const oldCount = bottleCountOf(getState().cart?.items);
  try {
    const cart = await api.removeCartItem({ cartId, variantIds: [variantId] });
    applyCart(cart);
    showCartAfterMutation(cart, oldCount, { fromCart, fromDrawer });
  } catch (e) {
    error("[cart] remove failed", e);
    showError();
  } finally {
    emit("ws-cart-busy", { active: false });
  }
}

let started = false;

export async function init({ cartRenderDelayMs: delay = 0 } = {}) {
  if (started) return;
  started = true;
  cartRenderDelayMs = delay;

  // Restore a persisted cart so the badge + wine cards reflect it on load.
  const stored = loadStoredCartId();
  if (stored) {
    setState({ cart: { ...getState().cart, id: stored } });
    api.getCart(stored).then(applyCart).catch(() => {
      /* stale cartId — leave it; next add will create a fresh cart */
    });
  }

  on("ws-cart-open", openCart);
  on("ws-cart-add", addItem);
  on("ws-cart-update", updateItem);
  on("ws-cart-remove", removeItem);
  // "Proceed to checkout" (ws-checkout) is owned by features/optional/checkout.js,
  // which runs the in-widget Ship/Pickup flow instead of redirecting.
}
